// Advanced features for the HTML Knowledge Graph
// This file contains the complex interactive features

export function generateAdvancedFeatures() {
  return `
    // Advanced 3D rotation and zoom controls
    let rotation = { x: 0, y: 0, z: 0 };
    let zoom = 1;
    let translation = { x: 0, y: 0 };
    let isAutoRotating = false;
    let autoRotateInterval = null;

    function apply3DTransform(x, y, z = 0) {
        const radX = (rotation.x * Math.PI) / 180;
        const radY = (rotation.y * Math.PI) / 180;
        const radZ = (rotation.z * Math.PI) / 180;

        let newX = x * Math.cos(radY) - z * Math.sin(radY);
        let newZ = x * Math.sin(radY) + z * Math.cos(radY);
        
        let newY = y * Math.cos(radX) - newZ * Math.sin(radX);
        newZ = y * Math.sin(radX) + newZ * Math.cos(radX);
        
        const finalX = newX * Math.cos(radZ) - newY * Math.sin(radZ);
        const finalY = newX * Math.sin(radZ) + newY * Math.cos(radZ);

        const perspective = 600;
        const projectedX = (finalX * perspective) / (perspective + newZ);
        const projectedY = (finalY * perspective) / (perspective + newZ);
        const scale = perspective / (perspective + newZ);

        return { 
            x: projectedX, 
            y: projectedY, 
            scale: Math.max(0.4, scale),
            depth: newZ 
        };
    }

    function rotateGraph(axis, degrees) {
        rotation[axis] = (rotation[axis] + degrees) % 360;
        updateRotationDisplay();
        renderGraph();
    }

    function toggleAutoRotate() {
        isAutoRotating = !isAutoRotating;
        const btn = document.getElementById('autoRotateBtn');
        const text = document.getElementById('autoRotateText');
        const status = document.getElementById('autoRotateStatus');

        if (isAutoRotating) {
            btn.classList.remove('bg-indigo-100', 'hover:bg-indigo-200', 'text-indigo-700');
            btn.classList.add('bg-indigo-600', 'text-white');
            text.textContent = 'Stop Auto Rotate';
            status.classList.remove('hidden');
            
            autoRotateInterval = setInterval(() => {
                rotation.z = (rotation.z + 1) % 360;
                updateRotationDisplay();
                renderGraph();
            }, 50);
        } else {
            btn.classList.add('bg-indigo-100', 'hover:bg-indigo-200', 'text-indigo-700');
            btn.classList.remove('bg-indigo-600', 'text-white');
            text.textContent = 'Auto Rotate';
            status.classList.add('hidden');
            
            if (autoRotateInterval) {
                clearInterval(autoRotateInterval);
                autoRotateInterval = null;
            }
        }
    }

    function changeZoom(delta) {
        zoom = Math.max(0.2, Math.min(3, zoom + delta));
        updateZoomDisplay();
        renderGraph();
    }

    function resetView() {
        zoom = 1;
        rotation = { x: 0, y: 0, z: 0 };
        translation = { x: 0, y: 0 };
        if (isAutoRotating) {
            toggleAutoRotate();
        }
        updateRotationDisplay();
        updateZoomDisplay();
        renderGraph();
    }

    function updateRotationDisplay() {
        const rotationX = document.getElementById('rotationX');
        const rotationY = document.getElementById('rotationY');
        const rotationZ = document.getElementById('rotationZ');
        
        if (rotationX) rotationX.textContent = rotation.x;
        if (rotationY) rotationY.textContent = rotation.y;
        if (rotationZ) rotationZ.textContent = rotation.z;
    }

    function updateZoomDisplay() {
        const zoomPercent = Math.round(zoom * 100);
        const zoomDisplay = document.getElementById('zoomDisplay');
        const zoomStatus = document.getElementById('zoomStatus');
        
        if (zoomDisplay) zoomDisplay.textContent = zoomPercent + '%';
        if (zoomStatus) zoomStatus.textContent = zoomPercent;
    }

    // Mouse interaction handlers
    let isDragging = false;
    let dragStart = { x: 0, y: 0 };

    function handleMouseDown(e) {
        isDragging = true;
        dragStart = { x: e.clientX, y: e.clientY };
        document.getElementById('graphSvg').classList.add('cursor-grabbing');
    }

    function handleMouseMove(e) {
        if (isDragging) {
            const deltaX = e.clientX - dragStart.x;
            const deltaY = e.clientY - dragStart.y;
            translation.x += deltaX * 0.5;
            translation.y += deltaY * 0.5;
            dragStart = { x: e.clientX, y: e.clientY };
            renderGraph();
        }
    }

    function handleMouseUp() {
        isDragging = false;
        document.getElementById('graphSvg').classList.remove('cursor-grabbing');
    }

    function handleWheel(e) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        changeZoom(delta);
    }

    // Initialize advanced controls
    function initializeAdvancedControls() {
        const svg = document.getElementById('graphSvg');
        if (svg) {
            svg.addEventListener('mousedown', handleMouseDown);
            svg.addEventListener('mousemove', handleMouseMove);
            svg.addEventListener('mouseup', handleMouseUp);
            svg.addEventListener('mouseleave', handleMouseUp);
            svg.addEventListener('wheel', handleWheel);
        }

        // Rotation buttons
        const rotateUpBtn = document.getElementById('rotateUpBtn');
        const rotateDownBtn = document.getElementById('rotateDownBtn');
        const rotateLeftBtn = document.getElementById('rotateLeftBtn');
        const rotateRightBtn = document.getElementById('rotateRightBtn');
        const rotateCCWBtn = document.getElementById('rotateCCWBtn');
        const rotateCWBtn = document.getElementById('rotateCWBtn');

        if (rotateUpBtn) rotateUpBtn.addEventListener('click', () => rotateGraph('x', -15));
        if (rotateDownBtn) rotateDownBtn.addEventListener('click', () => rotateGraph('x', 15));
        if (rotateLeftBtn) rotateLeftBtn.addEventListener('click', () => rotateGraph('y', -15));
        if (rotateRightBtn) rotateRightBtn.addEventListener('click', () => rotateGraph('y', 15));
        if (rotateCCWBtn) rotateCCWBtn.addEventListener('click', () => rotateGraph('z', -15));
        if (rotateCWBtn) rotateCWBtn.addEventListener('click', () => rotateGraph('z', 15));

        // Auto rotate button
        const autoRotateBtn = document.getElementById('autoRotateBtn');
        if (autoRotateBtn) autoRotateBtn.addEventListener('click', toggleAutoRotate);

        // Zoom controls
        const zoomInBtn = document.getElementById('zoomInBtn');
        const zoomOutBtn = document.getElementById('zoomOutBtn');
        if (zoomInBtn) zoomInBtn.addEventListener('click', () => changeZoom(0.2));
        if (zoomOutBtn) zoomOutBtn.addEventListener('click', () => changeZoom(-0.2));

        // Reset button
        const resetBtn = document.getElementById('resetBtn');
        if (resetBtn) resetBtn.addEventListener('click', resetView);
    }
  `;
} 